import {
  CoinVertical,
  Fire,
  Gear,
  Percent,
  SpeakerHigh,
  SpeakerX,
  SquareSplitHorizontal,
  SquareSplitVertical,
} from "@phosphor-icons/react";
import confetti, { type CreateTypes, type Shape } from "canvas-confetti";
import clsx from "clsx";
import { AnimatePresence, useReducedMotion } from "motion/react";
import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { preload } from "react-dom";
import { createHtmlPortalNode, OutPortal } from "react-reverse-portal";
import { toast } from "sonner";
import { match } from "ts-pattern";

import { useLocale } from "~/hooks/locale";
import { useTheme } from "~/hooks/theme";
import { colors } from "~/theme";
import type { FragmentType } from "~/utils/fragment";
import { useLocalStorage } from "~/utils/storage";

import { AudioClue } from "../AudioClue";
import { useGame } from "../GameContext";
import { GameLabel } from "../GameLabel";
import { ImageClue } from "../ImageClue";
import { TextDefinition } from "../TextDefinition";
import { Toaster } from "../Toaster";
import Choices from "./Choices";
import { Classic } from "./Classic";
import { CONFETTI_BLASTS, REWARD_TIME_PER_CORRECT_ANSWER } from "./const";
import type { Game_WordFragment } from "./Game_WordFragment";
import { GameMenu } from "./GameMenu";
import { GameMenuItem } from "./GameMenuItem";
import { GameOption } from "./GameOption";
import { GameStat } from "./GameStat";
import { Timer } from "./Timer";
import { type GameAnswer, getPoints, getStreak } from "./utils/answer";

const speak: React.MouseEventHandler<HTMLSpanElement> = (event) => {
  if (event.currentTarget.classList.contains("tooltip")) {
    const utterance = new SpeechSynthesisUtterance(
      event.currentTarget.parentElement?.innerText,
    );
    speechSynthesis.cancel();
    speechSynthesis.speak(utterance);
  }
};

export type GameWord = FragmentType<
  "Word",
  { select: typeof Game_WordFragment }
>;

export const Game: React.FC = () => {
  const { t } = useLocale();
  const { game, setGame, resetGame, playSound } = useGame();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isPercentShown, setIsPercentShown] = useState(
    game.settings.isPercentShown,
  );

  const [isRow, setIsRow] = useLocalStorage("row", true);
  const [isSoundEnabled, setIsSoundEnabled] = useLocalStorage("sound", true);

  const toggleSound = useCallback(() => {
    setIsSoundEnabled((prev) => !prev);
    playSound(isSoundEnabled ? "off" : "on");
  }, [isSoundEnabled, setIsSoundEnabled, playSound]);

  const closeMenu = useCallback(() => {
    setIsMenuOpen(false);
  }, []);

  const confettiRef = useRef<CreateTypes>(null);
  const prefersReducedMotion = useReducedMotion();
  const portalNode = useMemo(() => createHtmlPortalNode(), []);

  const [answers, setAnswers] = useState<GameAnswer[]>([]);
  const streak = useMemo(() => getStreak(answers), [answers]);
  const [hintUsedAt, setHintUsedAt] = useState(0);

  const hintPoints = useMemo(
    () => (game.settings.isHintEnabled ? streak - hintUsedAt : 0),
    [hintUsedAt, streak, game.settings.isHintEnabled],
  );

  const rewardTime = useMemo(
    () =>
      game.mode === "classic" && game.settings.isTimeRewardEnabled
        ? answers.filter((answer) => answer.correct).length *
          REWARD_TIME_PER_CORRECT_ANSWER *
          1000
        : 0,
    [game.mode, game.settings.isTimeRewardEnabled, answers],
  );

  const [startedAt, setStartedAt] = useState(Date.now());
  const endsAt = useMemo(
    () => startedAt + rewardTime + game.timeLimit * 1000,
    [startedAt, game.timeLimit, rewardTime],
  );

  const restartGame = useCallback(() => {
    setStartedAt(Date.now());
    setAnswers([]);
    setIsMenuOpen(false);
    toast.dismiss();
  }, []);

  const endGame = useCallback(() => {
    setGame((prev) => ({
      ...prev,
      time: Date.now() - startedAt,
      answers,
    }));
  }, [setGame, startedAt, answers]);

  const isImageMatch = useMemo(() => game.mode === "imcq", [game.mode]);

  const word = useMemo(
    // the game ends when the last word is answered
    // and the game can't be started if there are no words
    // so we know that this will always exist
    () => game.words[answers.length]!,
    [game.words, answers.length],
  );

  const imageClue = useMemo(
    () => word.definitions.find((definition) => definition.image),
    [word.definitions],
  );

  const audioClue = useMemo(
    () => word.definitions.find((definition) => definition.audio),
    [word.definitions],
  );

  useEffect(() => {
    const nextImage = game.words[answers.length + 1]?.definitions.find(
      (definition) => definition.image,
    )?.image;
    if (nextImage) {
      // preload the next image, if there is one
      preload(nextImage, { as: "image" });
    }
  }, [game.words, answers.length]);

  const [theme] = useTheme();

  const handleAnswer = useCallback(
    (answer: GameAnswer) => {
      if (answers.length === game.words.length - 1) {
        // if this is the last word, end the game
        setGame((prev) =>
          // if time is already set, the game is over
          // don't need to set it again
          // (and cause the post game screen to rerender)
          prev.time > 0
            ? prev
            : {
                ...prev,
                time: Date.now() - startedAt,
                answers: [...answers, answer],
              },
        );
        return;
      }

      // otherwise add the answer to the list
      setAnswers((prev) => [...prev, answer]);

      if (!answer.correct) {
        toast.error(answer.solution);
        return;
      }

      toast.success(answer.solution);

      // blast confetti 🎉
      if (confettiRef.current) {
        confettiRef.current.reset();

        const color = match(theme)
          .with("hearts", () => colors["can-can"])
          .otherwise(() => colors.dandelion);

        const shapes = match(theme)
          .returnType<Shape[]>()
          .with("hearts", () => {
            const heart = confetti.shapeFromPath(
              // a heart shape
              "M0 200V0h200a100 100 90 0 1 0 200 100 100 90 0 1-200 0z",
            );
            return [heart];
          })
          .otherwise(() => ["circle", "square"]);

        const scalar = match(theme)
          .with(
            "hearts",
            () => 1.5, // make the hearts bigger
          )
          .otherwise(() => 1);

        for (const blast of CONFETTI_BLASTS) {
          confettiRef.current({
            ...blast,
            spread: 90,
            shapes,
            scalar,
            colors: [color[50], color[100], color[200], color[300], color[400]],
          });
        }
      }
    },
    [game.words.length, answers, setGame, startedAt, theme],
  );

  const handleHintUsed = useCallback(() => {
    setHintUsedAt(streak);

    if (isSoundEnabled) {
      playSound("use_hint");
    }
  }, [streak, isSoundEnabled, playSound]);

  const isSpeechSynthesisAvailable = useMemo(
    () => "speechSynthesis" in window && "SpeechSynthesisUtterance" in window,
    [],
  );

  const [viewportHeight, setViewportHeight] = useState(visualViewport?.height);

  useEffect(() => {
    const handleResize = (event: Event) => {
      if (event.target instanceof VisualViewport) {
        setViewportHeight(event.target.height);
      }
    };

    visualViewport?.addEventListener("resize", handleResize);
    return () => {
      visualViewport?.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <>
      <div
        className="absolute inset-0 z-0 flex select-none flex-col bg-[image:var(--bg-image)] bg-cover bg-center sm:divide-y"
        style={{ height: viewportHeight }}
      >
        <div className="flex h-pad shrink-0 pad max-sm:hidden">
          <div className="relative grow border-x px-4 sm:px-6">
            <GameLabel position="bottom-left">{t("your_clue")}</GameLabel>
          </div>
        </div>
        <div className="flex min-h-0 grow sm:pad">
          <div className="relative flex grow">
            <div
              className={clsx(
                "grid grow grid-rows-2 divide-y bg-base-100/60 sm:border-x",
                isRow &&
                  "lg:grid-cols-2 lg:grid-rows-none lg:divide-x lg:divide-y-0",
                game.mode === "classic" && "max-md:grid-rows-[3fr_2fr]",
              )}
            >
              <div className="relative px-4 sm:px-6">
                {game.timeLimit > 0 && (
                  <GameLabel direction="up" position="bottom-right">
                    {t("time_remaining")}
                  </GameLabel>
                )}
                <div
                  className={clsx(
                    "flex size-full items-center justify-center text-center",
                    isImageMatch
                      ? "text-3xl sm:text-4xl md:text-5xl"
                      : "text-2xl sm:text-3xl md:text-4xl",
                  )}
                >
                  {audioClue?.audio ? (
                    <AudioClue src={audioClue.audio} autoPlay />
                  ) : imageClue?.image && !isImageMatch ? (
                    <ImageClue key={imageClue.id} definition={imageClue} />
                  ) : isImageMatch ? (
                    <span className="font-medium">{word.solution}</span>
                  ) : (
                    word.definitions[0] && (
                      <span
                        className={
                          isSpeechSynthesisAvailable ? "tooltip" : undefined
                        }
                        data-tip={t("read_aloud")}
                        onClick={speak}
                      >
                        <TextDefinition definition={word.definitions[0]} />
                      </span>
                    )
                  )}
                </div>
                <div className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-gradient-to-t from-base-100 p-4">
                  <div
                    className={clsx(
                      "flex items-center gap-4 lg:gap-6",
                      game.mode !== "classic" && "sm:px-2",
                    )}
                  >
                    {game.mode === "classic" && <OutPortal node={portalNode} />}
                    <GameStat label={t("streak")} icon={Fire} value={streak} />
                    <div
                      onClick={() => {
                        setIsPercentShown((prev) => !prev);
                      }}
                    >
                      {isPercentShown ? (
                        <GameStat
                          isReverse
                          label={t("percent_complete")}
                          icon={Percent}
                          value={Math.round(
                            (answers.length / game.words.length) * 100,
                          )}
                        />
                      ) : (
                        <GameStat
                          label={t("points")}
                          icon={CoinVertical}
                          value={getPoints(answers)}
                        />
                      )}
                    </div>
                  </div>
                  {game.timeLimit > 0 && (
                    <Timer
                      className="sm:px-2"
                      endsAt={endsAt}
                      onEnd={endGame}
                    />
                  )}
                </div>
              </div>
              <div className="relative">
                {match(game.mode)
                  .with("classic", () => (
                    <Classic
                      key={word.id}
                      word={word}
                      hintPoints={hintPoints}
                      portalNode={portalNode}
                      isFirstLetterShown={game.settings.isFirstLetterShown}
                      isSoundEnabled={isSoundEnabled}
                      isDisabled={isMenuOpen}
                      onAnswer={handleAnswer}
                      onHintUsed={handleHintUsed}
                    />
                  ))
                  .with("mcq", "imcq", () => (
                    <Choices
                      word={word}
                      onAnswer={handleAnswer}
                      isRow={isRow}
                      isDisabled={isMenuOpen}
                      isSoundEnabled={isSoundEnabled}
                    />
                  ))
                  .exhaustive()}
                <canvas
                  className="pointer-events-none absolute inset-0 size-full"
                  ref={(canvas) => {
                    if (canvas && !prefersReducedMotion) {
                      confettiRef.current = confetti.create(canvas, {
                        resize: true,
                      });
                    }
                  }}
                />
              </div>
            </div>
            <div className="absolute right-1 top-1 flex gap-0.5 -translate-x-px">
              <GameOption
                className="max-lg:hidden"
                icon={isRow ? SquareSplitVertical : SquareSplitHorizontal}
                onClick={() => {
                  setIsRow((prev) => !prev);
                }}
              />
              <GameOption
                onClick={toggleSound}
                icon={isSoundEnabled ? SpeakerHigh : SpeakerX}
              />
              <GameOption
                icon={Gear}
                className={clsx(
                  "transition-all",
                  isMenuOpen ? "opacity-0 scale-75" : "opacity-100",
                )}
                onClick={() => {
                  setIsMenuOpen(true);
                }}
              />
            </div>
          </div>
        </div>
        <div className="flex h-pad shrink-0 pad before:grow before:border-x max-sm:hidden" />
      </div>
      <div className="isolate">
        <Toaster position="top-center" />
      </div>
      <AnimatePresence>
        {isMenuOpen && (
          <GameMenu onClose={closeMenu}>
            <GameMenuItem onClick={closeMenu}>{t("close_menu")}</GameMenuItem>
            <GameMenuItem onClick={toggleSound}>
              {t(isSoundEnabled ? "disable_sounds" : "enable_sounds")}
            </GameMenuItem>
            <GameMenuItem onClick={restartGame}>
              {t("restart_game")}
            </GameMenuItem>
            <GameMenuItem onClick={resetGame}>{t("quit_game")}</GameMenuItem>
          </GameMenu>
        )}
      </AnimatePresence>
    </>
  );
};
